import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, Package, AlertTriangle, RotateCcw, Wallet, Star, Truck, Store, MapPin, ShieldCheck, Sparkles, ArrowRight, CheckCircle2, Percent, FileCheck2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PayoutHoldBanner } from "@/components/PayoutHoldBanner";

const STATUS_LABEL = {
  approved: ["Approved", "bg-emerald-100 text-emerald-700"],
  admin_review: ["Admin Review", "bg-blue-100 text-blue-700"],
  ai_review: ["AI Review", "bg-blue-100 text-blue-700"],
  documents_submitted: ["Documents Submitted", "bg-blue-100 text-blue-700"],
  documents_required: ["Documents Required", "bg-amber-100 text-amber-700"],
  additional_info_required: ["Additional Info Required", "bg-amber-100 text-amber-700"],
  not_started: ["Not Started", "bg-slate-100 text-slate-600"],
  rejected: ["Rejected", "bg-rose-100 text-rose-700"],
  suspended: ["Suspended", "bg-rose-100 text-rose-700"],
  expired: ["Expired", "bg-amber-100 text-amber-700"],
};

export default function SellerDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [catStatuses, setCatStatuses] = useState([]);
  const [comm, setComm] = useState(null);
  const [commDefault, setCommDefault] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const loadComm = () => {
    api.get("/marketplace/seller/commission").then((r) => setComm(r.data)).catch(() => setComm(null));
    api.get("/marketplace/seller/commissions").then((r) => setCommDefault(r.data?.default_rate)).catch(() => {});
  };
  useEffect(() => {
    api.get("/marketplace/seller/dashboard/summary").then((r) => setS(r.data)).catch(() => setS(null));
    api.get("/seller/categories").then((r) => setCatStatuses(r.data.categories || [])).catch(() => {});
    loadComm();
  }, []);

  const commRules = comm?.rules || [];
  const unaccepted = commRules.filter((r) => !r.accepted);
  const allAccepted = commRules.length > 0 && unaccepted.length === 0;
  const acceptedOn = (comm?.acceptances || [])[0]?.accepted_at?.slice(0, 10);
  const acceptTerms = async () => {
    setAccepting(true);
    try {
      const targets = unaccepted.length ? unaccepted : commRules;
      for (const r of targets) { try { await api.post("/marketplace/seller/commission/accept", { rule_id: r.rule_id }); } catch { /* skip */ } }
      loadComm();
    } finally { setAccepting(false); }
  };

  const approved = s?.approved;
  const stat = STATUS_LABEL[s?.status] || ["Pending", "bg-slate-100 text-slate-600"];
  const money = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

  const CARDS = [
    { label: "Total Sales", val: money(s?.total_sales), to: "/agent/reports" },
    { label: "Orders", val: s?.orders ?? 0, to: "/agent/orders" },
    { label: "Pending Orders", val: s?.pending_orders ?? 0, to: "/agent/orders" },
    { label: "Products", val: s?.products ?? 0, to: "/agent/products" },
    { label: "Low Stock", val: s?.low_stock ?? 0, to: "/agent/products", icon: AlertTriangle },
    { label: "Returns", val: s?.returns ?? 0, to: "/agent/orders", icon: RotateCcw },
    { label: "Refunds", val: s?.refunds ?? 0, to: "/agent/orders", icon: RotateCcw },
    { label: "Pending Settlement", val: money(s?.pending_settlement), to: "/agent/settlements", icon: Wallet },
    { label: "Available Settlement", val: money(s?.available_settlement), to: "/agent/settlements", icon: Wallet },
    { label: "Product Reviews", val: s?.product_reviews ?? 0, to: "/agent/reviews", icon: Star },
  ];

  const LINKS = [
    { label: "Orders", icon: ShoppingBag, to: "/agent/orders" },
    { label: "Products", icon: Package, to: "/agent/products" },
    { label: "Shipping", icon: Truck, to: "/agent/shipping" },
    { label: "Storefront", icon: Store, to: "/agent/storefront" },
    { label: "Selling Areas", icon: MapPin, to: "/agent/selling-areas" },
    { label: "Reviews", icon: Star, to: "/agent/reviews" },
    { label: "Settlements", icon: Wallet, to: "/agent/settlements" },
    { label: "Credit", icon: Wallet, to: "/agent/credit" },
    { label: "Compliance", icon: ShieldCheck, to: "/agent/verification" },
  ];

  return (
    <div className="space-y-6" data-testid="seller-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-extrabold text-slate-900"><LayoutDashboard className="h-6 w-6 text-blue-500" /> Welcome, {(user?.name || "Seller").split(" ")[0]}</h1>
          <p className="text-sm text-slate-500">Your marketplace business at a glance.</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${stat[1]}`} data-testid="seller-status">{stat[0]}</span>
      </div>

      {(commDefault != null || commRules.length > 0) && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center" data-testid="commission-terms-card">
          <div className="flex items-center justify-center gap-2 text-sm font-bold text-slate-800"><Percent className="h-4 w-4 text-blue-500" /> Commission structure</div>
          <p className="mx-auto mt-1 max-w-xl text-xs text-slate-500">This is what Suffle charges on your sales. Historical orders keep their original rate.</p>
          <div className="mx-auto mt-3 flex max-w-2xl flex-wrap items-center justify-center gap-2" data-testid="commission-rate-list">
            {commDefault != null && <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">Marketplace default: {commDefault}%</span>}
            {commRules.map((r) => (
              <span key={r.rule_id} className="rounded-full bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200" data-testid={`commission-rule-${r.rule_id}`}>
                {r.scope_label || r.scope || "Rule"}: {String(r.comm_type || "").includes("flat") ? `₹${r.value}` : `${r.value}%`}
              </span>
            ))}
          </div>
          {allAccepted ? (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600" data-testid="commission-accepted"><CheckCircle2 className="h-4 w-4" /> You accepted these commission terms{acceptedOn ? ` on ${acceptedOn}` : ""}</p>
          ) : commRules.length > 0 ? (
            <button onClick={acceptTerms} disabled={accepting} data-testid="commission-accept-btn" className="mt-3 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
              <FileCheck2 className="h-4 w-4" /> {accepting ? "Saving…" : "Accept commission terms"}
            </button>
          ) : null}
          <div className="mt-2"><button onClick={() => nav("/agent/commission")} className="text-xs font-semibold text-blue-600" data-testid="commission-view-details">View full commission details</button></div>
        </div>
      )}

      <PayoutHoldBanner />

      {s && !approved && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="verification-banner">
          <p className="text-sm font-medium text-amber-800">Your seller account is {stat[0].toLowerCase()}. Complete verification to start selling and publishing products.</p>
          <button onClick={() => nav("/agent/verification")} data-testid="complete-verification-btn" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">Complete Verification <ArrowRight className="h-4 w-4" /></button>
        </div>
      )}
      {s && approved && (
        <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-700" data-testid="approved-banner"><CheckCircle2 className="h-4 w-4" /> Your seller account is approved — full selling functionality is enabled.</div>
      )}

      {catStatuses.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="seller-category-board">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-800">Your selling categories</h2>
            <button onClick={() => nav("/agent/verification")} className="text-xs font-semibold text-blue-600">Manage</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {catStatuses.map((c) => (
              <span key={c.category_id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-xs text-slate-700 ring-1 ring-slate-200" data-testid={`seller-cat-status-${c.category_id}`}>
                {c.name}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold capitalize ${c.status === "approved" ? "bg-emerald-100 text-emerald-700" : c.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{c.status}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {CARDS.map((c) => (
          <button key={c.label} onClick={() => nav(c.to)} data-testid={`seller-stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:shadow-md">
            <p className="text-xl font-extrabold text-slate-900">{s ? c.val : "—"}</p>
            <p className="text-xs text-slate-500">{c.label}</p>
          </button>
        ))}
      </div>

      <div>
        <h2 className="mb-3 text-lg font-bold text-slate-800">Manage your store</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {LINKS.map((l) => (
            <button key={l.label} onClick={() => nav(l.to)} data-testid={`seller-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-700 transition hover:border-blue-300 hover:shadow-sm"><l.icon className="h-5 w-5 text-blue-500" /> {l.label}</button>
          ))}
        </div>
      </div>

      <button onClick={() => nav("/agent/ai")} data-testid="seller-ai-entry" className="flex w-full items-center gap-3 rounded-2xl bg-gradient-to-br from-blue-50 via-violet-50 to-fuchsia-50 p-5 text-left">
        <Sparkles className="h-6 w-6 text-fuchsia-500" />
        <div><p className="font-bold text-slate-800">Ask Suffle AI about your business</p><p className="text-sm text-slate-500">"Which products are low in stock?" · "Which orders need shipping?" · "Which settlements are pending?"</p></div>
        <ArrowRight className="ml-auto h-5 w-5 text-slate-400" />
      </button>
    </div>
  );
}
